import { createContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  DEFAULT_DRIVER_SAFETY_MAP_PREF,
  normalizeDriverSafetyMapPrefs,
} from "src/utils/driverSafetyMapPrefs";

const OVERLAYS = { NORMAL: "normal", SATELITE: "satelite" };

const initialUserMapPref = {
  overlay: OVERLAYS.NORMAL,
  customGeofences: false,
  jobsiteGeofences: false,
  vehicleLabels: false,
  activeStopZones: true,
  railmap: false,
  driverSafetyMap: normalizeDriverSafetyMapPrefs(
    DEFAULT_DRIVER_SAFETY_MAP_PREF
  ),
};

const normalizeUserMapPref = (userMapPref) => ({
  ...initialUserMapPref,
  ...(userMapPref || {}),
  driverSafetyMap: normalizeDriverSafetyMapPrefs(userMapPref?.driverSafetyMap),
});

export const storeUserMapPref = (userMapPref) => {
  window.localStorage.setItem("user_map_pref", JSON.stringify(userMapPref));
};

export const restoreUserMapPref = () => {
  let userMapPref = null;

  try {
    const storedData = window.localStorage.getItem("user_map_pref");

    if (storedData) {
      userMapPref = normalizeUserMapPref(JSON.parse(storedData));
    } else {
      userMapPref = normalizeUserMapPref();
    }
  } catch (err) {
    console.error(err);
  }

  return userMapPref;
};

const UserMapPrefContext = createContext({
  userMapPref: { ...initialUserMapPref },
  saveUserMapPref: () => {},
  addressSearch: { latitude: null, longitude: null },
  searchAddress: () => {},
});

export const UserMapPrefProvider = (props) => {
  const { children } = props;
  const [userMapPref, setUserMapPref] = useState(initialUserMapPref);
  const [addressSearch, setAddressSearch] = useState({
    latitude: null,
    longitude: null,
  });

  useEffect(() => {
    const restoredUserMapPref = restoreUserMapPref();

    if (restoredUserMapPref) {
      setUserMapPref({ ...restoredUserMapPref });
    }
  }, []);

  const saveUserMapPref = (updatedUserMapPref) => {
    const mergedDriverSafetyMap = {
      ...(userMapPref?.driverSafetyMap || {}),
      ...(updatedUserMapPref?.driverSafetyMap || {}),
      styles: {
        ...((userMapPref?.driverSafetyMap || {}).styles || {}),
        ...((updatedUserMapPref?.driverSafetyMap || {}).styles || {}),
      },
    };

    const newUserMapPref = normalizeUserMapPref({
      ...userMapPref,
      ...updatedUserMapPref,
      driverSafetyMap: mergedDriverSafetyMap,
    });

    setUserMapPref({ ...newUserMapPref });
    storeUserMapPref({ ...newUserMapPref });
  };

  const searchAddress = (address) => {
    setAddressSearch({ ...address });
  };

  return (
    <UserMapPrefContext.Provider
      value={{
        userMapPref,
        saveUserMapPref,
        addressSearch,
        searchAddress,
      }}
    >
      {children}
    </UserMapPrefContext.Provider>
  );
};

UserMapPrefProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const UserMapPrefConsumer = UserMapPrefContext.Consumer;

export default UserMapPrefContext;
